// src/components/Analytics.jsx
import React, { useState, useEffect } from 'react';
import config from '../config';
import './Analytics.css';

const Analytics = () => {
  const costPerPdf = parseFloat(config.COST_PER_PDF)
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeMessage, setRechargeMessage] = useState('');
  const [rechargeError, setRechargeError] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [showUpdateRate, setShowUpdateRate] = useState(false);
  const [newRate, setNewRate] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`${config.API_BASE_URL}/api/analytics/`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch analytics');
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      // console.error('Error fetching analytics:', error);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    setError('');

    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`${config.API_BASE_URL}/api/upgrade/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setRechargeMessage(data.message);
        fetchAnalytics();
        setTimeout(() => setRechargeMessage(''), 3000);
      } else {
        setError(data.error || 'Upgrade failed');
      }
    } catch (error) {
      // console.error('Error upgrading:', error);
      setError('Network error. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  // Verify payment on backend after Razorpay success
  const verifyPayment = async (paymentResponse, rzp_amount, amount) => {
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`${config.API_BASE_URL}/api/verify-razorpay-payment/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          order_id: paymentResponse.razorpay_order_id,
          payment_id: paymentResponse.razorpay_payment_id,
          signature: paymentResponse.razorpay_signature,
          rzp_amount: rzp_amount,
          amount: amount
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setRechargeMessage(`Successfully recharged $${amount}! New balance: $${data.new_balance}`);
        setShowRecharge(false);
        setRechargeAmount('');
        fetchAnalytics();
        setTimeout(() => setRechargeMessage(''), 3000);
      } else {
        setRechargeError(data.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setRechargeError('Payment verification failed. Please contact support.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    setRechargeError('');
    setRechargeMessage('');

    const amount = parseFloat(rechargeAmount);
    if (isNaN(amount) || amount <= 0) {
      setRechargeError('Please enter a valid amount greater than 0');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Get CSRF token
      const csrfToken = getCookie('csrftoken');
      // 1. Call backend to create Razorpay order
      const rzp_response = await fetch(`${config.API_BASE_URL}/api/create-razorpay-order/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,  // Add CSRF token here
        },
        credentials: 'include',
        body: JSON.stringify({ amount })
      });

      const rzp_data = await rzp_response.json();

      if (!rzp_response.ok) {
        throw new Error(rzp_data.error || 'Failed to create payment order');
      }

      // 2. Initialize Razorpay checkout
      const options = {
        key: config.RAZORPAY_KEY_ID,
        amount: rzp_data.amount,
        currency: rzp_data.currency,
        order_id: rzp_data.order_id,
        name: config.APP_NAME,
        description: `Add $${amount} to your wallet`,
        handler: (response) => {
          verifyPayment(response, rzp_data.amount, amount);
        },
        prefill: {
          name: analytics?.user_name || '',
          email: analytics?.user_email || '',
        },
        theme: {
          color: '#667eea',
        },
        modal: {
          ondismiss: () => {
            setIsProcessingPayment(false);
            setRechargeError('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Error initiating payment:', error);
      setRechargeError(error.message || 'Failed to initiate payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // const handleRecharge = async (e) => {
  //   e.preventDefault();
  //   setRechargeError('');
  //   setRechargeMessage('');

  //   const amount = parseFloat(rechargeAmount);
  //   if (isNaN(amount) || amount <= 0) {
  //     setRechargeError('Please enter a valid amount greater than 0');
  //     return;
  //   }

  //   try {
  //     // 1. Call YOUR backend to create the order
  //     const rzp_response = await fetch(`${config.API_BASE_URL}/api/create-razorpay-order/`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ amount })
  //     });

  //     const { order_id, amount: orderAmount, currency } = await rzp_response.json();

  //     // 2. Initialize Razorpay with ONLY the public key_id (safe in browser)
  //     const options = {
  //       key: `${config.RAZORPAY_KEY_ID}`,  // ✅ Safe to expose
  //       amount: orderAmount,
  //       currency: currency,
  //       order_id: order_id,
  //       name: `${config.APP_NAME}`,
  //       handler: (response) => {
  //         // 3. Verify payment on your backend
  //         verifyPayment(response, amount);
  //       }
  //     };

  //     const razorpay = new window.Razorpay(options);
  //     razorpay.open();

  //     const csrfToken = getCookie('csrftoken');
  //     const response = await fetch(`${config.API_BASE_URL}/api/recharge/`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'X-CSRFToken': csrfToken,
  //       },
  //       credentials: 'include',
  //       body: JSON.stringify({ amount: amount }),
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       setRechargeMessage(`Successfully recharged $${amount}! New balance: $${data.new_balance}`);
  //       setShowRecharge(false);
  //       setRechargeAmount('');
  //       fetchAnalytics();
  //       setTimeout(() => setRechargeMessage(''), 3000);
  //     } else {
  //       setRechargeError(data.error || 'Recharge failed');
  //     }
  //   } catch (error) {
  //     // console.error('Error recharging:', error);
  //     setRechargeError('Network error. Please try again.');
  //   }
  // };

  const handleUpdateRate = async (e) => {
    e.preventDefault();

    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate <= 0) {
      setError('Please enter a valid rate greater than 0');
      return;
    }

    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`${config.API_BASE_URL}/api/update-pdf-rate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ rate: rate }),
      });

      const data = await response.json();

      if (response.ok) {
        setRechargeMessage(data.message);
        setShowUpdateRate(false);
        setNewRate('');
        fetchAnalytics();
        setTimeout(() => setRechargeMessage(''), 3000);
      } else {
        setError(data.error || 'Update failed');
      }
    } catch (error) {
      // console.error('Error updating rate:', error);
      setError('Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={fetchAnalytics} className="retry-btn">Try Again</button>
      </div>
    );
  }

  const maxBarHeight = Math.max(...(analytics.daily_pdfs?.map(d => d.count) || [1]), 1);

  return (
    <div className="analytics-container">
      {/* <h1>Analytics Dashboard</h1>
       */}
      {/* Success/Error Messages */}
      {rechargeMessage && (
        <div className="success-message">
          <span>✓</span> {rechargeMessage}
        </div>
      )}

      {/* Stats Cards - Updated with new fields */}
      <div className="analytics-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{analytics.profile.total_templates_created || 0}</h3>
            <p>Total Templates</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📄</div>
          <div className="stat-info">
            <h3>{analytics.profile.total_pdfs_generated || 0}</h3>
            <p>Total PDFs Generated</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <h3>${analytics.total_spend?.toFixed(2) || '0.00'}</h3>
            <p>Total Spend</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-info">
            <h3 className={analytics.profile.plan === 'free' ? 'free-plan' : 'usage-plan'}>
              {analytics.profile.plan === 'free' ? 'Free Plan' : 'Usage Plan'}
            </h3>
            <p>Current Plan</p>
          </div>
        </div>
      </div>

      {/* Daily PDF Generation Graph */}
      <div className="spending-section">
        <h2>Daily PDF Generation (Last 30 Days)</h2>
        <div className="chart-container">
          {!analytics.daily_pdfs || analytics.daily_pdfs.length === 0 ? (
            <div className="no-data">No PDFs generated in the last 30 days</div>
          ) : (
            <div className="bar-chart">
              {analytics.daily_pdfs.map((item, index) => (
                <div key={index} className="bar-item">
                  <div
                    className="bar"
                    style={{
                      height: `${(item.count / maxBarHeight) * 150}px`
                    }}
                  >
                    <span className="bar-value">{item.count}</span>
                  </div>
                  <span className="bar-label">
                    {new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Plan Details Section - Updated with new fields */}
      <div className="usage-section">
        <h2>Plan Details</h2>
        <div className="usage-card">
          <div className={`plan-badge ${analytics.profile.plan === 'usage' ? 'usage-badge' : 'free-badge'}`}>
            {analytics.profile.plan === 'free' ? 'Free Plan' : 'Usage Plan'}
          </div>

          {/* Free Plan Display */}
          {analytics.profile.plan === 'free' && (
            <div className="usage-details">
              <div className="usage-item">
                <span>📝 Templates Used:</span>
                <span className={analytics.profile.free_templates_used >= analytics.profile.free_template_limit ? 'limit-warning' : ''}>
                  {analytics.profile.free_templates_used} / {analytics.profile.free_template_limit}
                </span>
                <span className="remaining">
                  ({analytics.profile.remaining_free_templates} remaining)
                </span>
              </div>
              <div className="usage-item">
                <span>📄 PDFs Generated:</span>
                <span className={analytics.profile.free_pdfs_generated >= analytics.profile.free_pdf_limit ? 'limit-warning' : ''}>
                  {analytics.profile.free_pdfs_generated} / {analytics.profile.free_pdf_limit}
                </span>
                <span className="remaining">
                  ({analytics.profile.remaining_free_pdfs} remaining)
                </span>
              </div>
              <div className="usage-item">
                <span>💰 Cost per PDF:</span>
                <span>Free (up to limit)</span>
              </div>
              <div className="usage-item">
                <span>📈 Lifetime Templates:</span>
                <span>{analytics.profile.total_templates_created}</span>
              </div>
              <div className="usage-item">
                <span>📈 Lifetime PDFs:</span>
                <span>{analytics.profile.total_pdfs_generated}</span>
              </div>

              {(analytics.profile.free_templates_used >= analytics.profile.free_template_limit ||
                analytics.profile.free_pdfs_generated >= analytics.profile.free_pdf_limit) && (
                  <div className="upgrade-message">
                    <p>⚠️ You have reached your free plan limit. Upgrade to continue using {config.APP_NAME}!</p>
                    <button onClick={handleUpgrade} disabled={upgrading} className="upgrade-btn">
                      {upgrading ? 'Upgrading...' : 'Upgrade to Usage Plan'}
                    </button>
                  </div>
                )}
            </div>
          )}

          {/* Usage Plan Display - Updated with rate management */}
          {analytics.profile.plan === 'usage' && (
            <div className="usage-details">
              <div className="usage-item">
                <span>💰 Current Balance:</span>
                <span className="balance-amount">${analytics.profile.balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="usage-item">
                <span>💵 Cost per PDF:</span>
                <span>
                  ${analytics.profile.pdf_generation_rate?.toFixed(4) || costPerPdf} per generation
                </span>
              </div>

              {showUpdateRate && (
                <form onSubmit={handleUpdateRate} className="update-rate-form">
                  <div className="update-rate-input-group">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="New rate"
                      value={newRate}
                      onChange={(e) => setNewRate(e.target.value)}
                      required
                      className="update-rate-input"
                    />
                  </div>
                  <button type="submit" className="submit-rate-btn">Update Rate</button>
                  <button type="button" onClick={() => setShowUpdateRate(false)} className="cancel-rate-btn">Cancel</button>
                </form>
              )}

              <div className="usage-item">
                <span>📝 Templates Created:</span>
                <span>{analytics.profile.total_templates_created || 0} (Unlimited)</span>
              </div>
              <div className="usage-item">
                <span>📄 Total PDFs Generated:</span>
                <span>{analytics.profile.total_pdfs_generated || 0}</span>
              </div>

              {analytics.profile.balance < costPerPdf && (
                <div className="low-balance-warning">
                  <p>⚠️ Low balance! Please recharge to continue generating PDFs.</p>
                </div>
              )}

              <div className="recharge-section">
                <button onClick={() => setShowRecharge(!showRecharge)} className="recharge-btn">
                  {showRecharge ? 'Cancel' : 'Add Balance'}
                </button>

                {showRecharge && (
                  <form onSubmit={handleRecharge} className="recharge-form">
                    <div className="recharge-input-group">
                      <span className="currency-symbol">$</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="Amount"
                        value={rechargeAmount}
                        onChange={(e) => setRechargeAmount(e.target.value)}
                        required
                        className="recharge-input"
                      />
                    </div>
                    {rechargeError && <p className="recharge-error">{rechargeError}</p>}
                    <button type="submit" className="submit-recharge-btn">Recharge</button>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spending Summary */}
      <div className="balance-section">
        <h2>Spending Summary</h2>
        <div className="balance-card">
          <div className="balance-amount">
            <span className="balance-label">Total Spend (Lifetime)</span>
            <span className="balance-value">${analytics.total_spend?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="balance-amount">
            <span className="balance-label">Total PDFs Generated</span>
            <span className="balance-value">{analytics.total_pdfs || 0}</span>
          </div>
          <div className="balance-amount">
            <span className="balance-label">Average Cost per PDF</span>
            <span className="balance-value">
              {analytics.total_pdfs > 0
                ? `$${((analytics.total_spend || 0) / analytics.total_pdfs).toFixed(4)}`
                : '$0.00'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;